import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarPlus, CheckCircle2, Pencil, UserPlus, Users } from "lucide-react";

import { alumniService, type Alumni, type Event, type Options } from "../../services/alumniService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { AddGraduatesForm, EventForm, ProfileForm, RegisterForm } from "./AlumniForms";
import { EVENT_TYPES, alumniConfig, canRegister, day, eventConfig, eventState, labelOf, paymentLabel, time, ugx } from "./alumniLogic";

const work = (row: Alumni) => [row.job_title || row.current_occupation, row.current_company].filter(Boolean).join(", ");

/** The directory of former pupils: the office's view of everyone, with what each has given and done. */
export function DirectoryPanel({ rows, options, isLoading, isError, onRetry, onChanged, graduatesWaiting, initialView }: {
  rows?: Alumni[];
  options?: Options;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  graduatesWaiting: number;
  initialView?: string;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [form, setForm] = useState<"graduates" | "manual" | "edit" | null>(null);
  const graduates = useQuery({ queryKey: ["alumni", "graduates"], queryFn: alumniService.getGraduates, enabled: form === "graduates" });
  const done = (text: string) => { setForm(null); setNotice({ tone: "success", text }); onChanged(); };
  const addGraduates = useMutation({ mutationFn: alumniService.addGraduates, onSuccess: (result) => done(`${result.created_count} ${result.created_count === 1 ? "graduate" : "graduates"} added to the directory.`) });
  const save = useMutation({
    mutationFn: ({ id, data }: { id?: number; data: Record<string, unknown> }) => (id ? alumniService.updateAlumni(id, data) : alumniService.createAlumni(data)),
    onSuccess: (saved, { id }) => done(`${saved.user_name} ${id ? "saved" : "added to the directory"}.`),
  });
  const toggle = useMutation({
    mutationFn: (row: Alumni) => alumniService.updateAlumni(row.id, { is_active: !row.is_active }),
    onSuccess: (saved) => { setViewing(null); done(`${saved.user_name} is now ${saved.is_active ? "active" : "archived"} in the directory.`); },
  });
  const columns: RegisterColumn<Alumni>[] = [
    { header: "Name", cell: (row) => <span className="font-medium text-gray-900">{row.user_name}</span>, exportValue: (row) => row.user_name },
    { header: "Class of", cell: (row) => row.graduation_year, exportValue: (row) => row.graduation_year },
    { header: "Final class", cell: (row) => row.graduation_class || "—", exportValue: (row) => row.graduation_class ?? "", exportOnly: true },
    { header: "Work", cell: (row) => work(row) || <span className="text-gray-400">Not listed</span>, exportValue: (row) => work(row) },
    { header: "Phone", cell: (row) => row.phone_number || "—", exportValue: (row) => row.phone_number ?? "" },
    { header: "Email", cell: (row) => row.email || "—", exportValue: (row) => row.email ?? "", exportOnly: true },
    { header: "Given", cell: (row) => (row.donation_count ? `${row.donation_count} · ${ugx(row.donation_total)}` : "—"), exportValue: (row) => Number(row.donation_total ?? 0) },
    { header: "Events", cell: (row) => row.events_attended || "—", exportValue: (row) => row.events_attended ?? 0, align: "right" },
    { header: "Mentoring", cell: (row) => row.mentorship_count || "—", exportValue: (row) => row.mentorship_count ?? 0, align: "right" },
    { header: "Status", cell: (row) => <StatusDot label={row.is_active ? (row.is_volunteer ? "Active · volunteer" : "Active") : "Archived"} dot={row.is_active ? "bg-emerald-500" : "bg-gray-400"} />, exportValue: (row) => (row.is_active ? "Active" : "Archived") },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {graduatesWaiting > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-900">
          <span><span className="font-semibold">{graduatesWaiting} {graduatesWaiting === 1 ? "graduate is" : "graduates are"}</span> not in the directory yet.</span>
          <button type="button" className="btn btn-primary h-8 gap-1.5 text-xs" onClick={() => { addGraduates.reset(); setForm("graduates"); }}><UserPlus className="h-3.5 w-3.5" /> Add graduates</button>
        </div>
      )}
      <ClientRegister
        key={initialView}
        title="Alumni directory"
        description="Former pupils: when they left, what they do now, how to reach them, and what they have given and done for the school."
        actions={
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-outline h-9 gap-1.5 text-sm" onClick={() => { save.reset(); setForm("manual"); }}><Users className="h-4 w-4" /> Add a former pupil</button>
            <button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" onClick={() => { addGraduates.reset(); setForm("graduates"); }}><UserPlus className="h-4 w-4" /> Add graduates</button>
          </div>
        }
        rows={rows}
        config={alumniConfig}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        noun={["former pupil", "former pupils"]}
        exportName={{ file: "alumni-directory", title: "Alumni Directory" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No former pupils in the directory yet. Add the school's graduates to start."
      />
      {row && !form && (
        <DetailsModal
          size="3xl"
          title={row.user_name}
          headline={{ label: `Class of ${row.graduation_year}${row.graduation_class ? ` · ${row.graduation_class}` : ""}`, value: work(row) || "Work not listed", note: row.industry ?? undefined }}
          status={{ label: row.is_active ? "Active" : "Archived", dot: row.is_active ? "bg-emerald-500" : "bg-gray-400" }}
          facts={[
            ["Student ID", row.student_id],
            ["Phone", row.phone_number || "—"],
            ["Email", [row.email, row.alternative_email].filter(Boolean).join(", ") || "—"],
            ["LinkedIn", row.linkedin_profile ? <a href={row.linkedin_profile} target="_blank" rel="noreferrer" className="text-primary-700 hover:underline">Profile</a> : "—"],
            ["Given", row.donation_count ? `${row.donation_count} gifts · ${ugx(row.donation_total)} in money` : "Nothing yet"],
            ["Events attended", String(row.events_attended ?? 0)],
            ["Mentorships", String(row.mentorship_count ?? 0)],
            ["Volunteer", row.is_volunteer ? "Yes" : "No"],
          ]}
          sections={[
            { title: "Where they live", body: row.current_address },
            { title: "Further education", body: [row.higher_education, row.degrees_earned, row.certifications].filter(Boolean).join("\n") },
            { title: "Achievements", body: row.achievements },
            { title: "Interests", body: row.interests },
            { title: "Office notes", body: row.notes },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" disabled={toggle.isPending} className="mr-auto btn btn-outline" onClick={() => toggle.mutate(row)}>{row.is_active ? "Archive" : "Make active"}</button>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              <button type="button" className="btn btn-primary gap-1.5" onClick={() => { save.reset(); setForm("edit"); }}><Pencil className="h-4 w-4" /> Edit</button>
            </>
          }
        />
      )}
      {form === "graduates" && (graduates.isLoading ? null : <AddGraduatesForm graduates={graduates.data ?? []} busy={addGraduates.isPending} error={addGraduates.isError ? apiErrorMessage(addGraduates.error, "The graduates could not be added.") : graduates.isError ? "The graduates could not be loaded." : undefined} onSave={(ids) => addGraduates.mutate(ids)} onClose={() => setForm(null)} />)}
      {form === "manual" && <ProfileForm isOffice options={options} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The former pupil could not be added.") : undefined} onSave={(data) => save.mutate({ data })} onClose={() => setForm(null)} />}
      {form === "edit" && row && <ProfileForm isOffice record={row} options={options} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The profile could not be saved.") : undefined} onSave={(data) => save.mutate({ id: row.id, data })} onClose={() => setForm(null)} />}
    </div>
  );
}

/** An alumnus's own profile, as they see it. */
export function MyProfilePanel({ profile, onChanged }: { profile?: Alumni; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<NoticeState>(null);
  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) => alumniService.updateAlumni(profile!.id, data),
    onSuccess: () => { setEditing(false); setNotice({ tone: "success", text: "Your profile is saved." }); onChanged(); },
  });
  if (!profile) {
    return <p className="rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-900">Your account is not linked to an alumni profile yet. The school adds former pupils to the directory once their graduation is recorded; ask the school office if you left before that.</p>;
  }
  const rows: [string, string][] = [
    ["Class of", `${profile.graduation_year}${profile.graduation_class ? ` · ${profile.graduation_class}` : ""}`],
    ["Work", work(profile) || "Not listed"],
    ["Phone", profile.phone_number || "—"],
    ["Email", profile.email || "—"],
    ["Further education", profile.higher_education || "—"],
    ["Interests", profile.interests || "—"],
  ];
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <section className="card">
        <div className="card-header flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-gray-900">{profile.user_name}</h3>
            <p className="mt-1 text-sm text-gray-500">What the school knows about you now. Keep it up to date so we can invite you to reunions and match you with learners you could mentor.</p>
          </div>
          <button type="button" className="btn btn-primary gap-1.5" onClick={() => { save.reset(); setEditing(true); }}><Pencil className="h-4 w-4" /> Update my profile</button>
        </div>
        <dl className="card-content grid gap-x-6 sm:grid-cols-2">
          {rows.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4 border-b border-gray-100 py-2">
              <dt className="text-sm text-gray-500">{label}</dt>
              <dd className="text-right text-sm font-medium text-gray-900">{value}</dd>
            </div>
          ))}
        </dl>
      </section>
      {editing && <ProfileForm isOffice={false} record={profile} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "Your profile could not be saved.") : undefined} onSave={(data) => save.mutate(data)} onClose={() => setEditing(false)} />}
    </div>
  );
}

/** Reunions and other events: the office runs them; alumni register. */
export function EventsPanel({ rows, isOffice, hasProfile, isLoading, isError, onRetry, onChanged }: {
  rows?: Event[];
  isOffice: boolean;
  hasProfile: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [form, setForm] = useState<"new" | "edit" | "register" | null>(null);
  const done = (text: string) => { setForm(null); setNotice({ tone: "success", text }); onChanged(); };
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  const registrations = useQuery({ queryKey: ["alumni", "registrations", viewing], queryFn: () => alumniService.getRegistrations(viewing!), enabled: isOffice && Boolean(viewing) });
  const save = useMutation({
    mutationFn: ({ id, data }: { id?: number; data: Record<string, unknown> }) => (id ? alumniService.updateEvent(id, data) : alumniService.createEvent(data)),
    onSuccess: (saved, { id }) => done(`${saved.name} ${id ? "saved" : "created"}${saved.is_published ? "" : " as a draft"}.`),
  });
  const register = useMutation({ mutationFn: alumniService.register, onSuccess: (saved) => done(`You're registered for ${saved.event_name}.`) });
  const cancel = useMutation({ mutationFn: alumniService.cancelRegistration, onSuccess: () => { setViewing(null); done("Your registration is cancelled."); }, onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "It could not be cancelled.") }) });
  const act = useMutation({
    mutationFn: ({ id, action }: { id: number; action: "paid" | "in" }) => (action === "paid" ? alumniService.markPaid(id) : alumniService.checkIn(id)),
    onSuccess: () => { registrations.refetch(); onChanged(); },
    onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "That could not be done.") }),
  });
  const setActive = useMutation({ mutationFn: (event: Event) => alumniService.updateEvent(event.id, { is_active: !event.is_active }), onSuccess: (saved) => { setViewing(null); done(`${saved.name} ${saved.is_active ? "restored" : "cancelled"}.`); } });

  const columns: RegisterColumn<Event>[] = [
    { header: "Event", cell: (item) => <span className="font-medium text-gray-900">{item.name}</span>, exportValue: (item) => item.name },
    { header: "Kind", cell: (item) => labelOf(EVENT_TYPES, item.event_type), exportValue: (item) => labelOf(EVENT_TYPES, item.event_type) },
    { header: "When", cell: (item) => `${day(item.event_date)} · ${time(item.start_time)}–${time(item.end_time)}`, exportValue: (item) => `${day(item.event_date)} ${time(item.start_time)}-${time(item.end_time)}` },
    { header: "Venue", cell: (item) => item.venue || "—", exportValue: (item) => item.venue ?? "" },
    { header: "Fee", cell: (item) => (Number(item.registration_fee) ? ugx(item.registration_fee) : "Free"), exportValue: (item) => Number(item.registration_fee) },
    ...(isOffice
      ? [{ header: "Registered", cell: (item: Event) => `${item.registered_count ?? 0}${item.max_attendees ? ` / ${item.max_attendees}` : ""}${item.unpaid_count ? ` · ${item.unpaid_count} unpaid` : ""}`, exportValue: (item: Event) => item.registered_count ?? 0 }]
      : [{ header: "You", cell: (item: Event) => (item.my_registration ? <span className="text-emerald-700">Registered</span> : "—"), exportValue: (item: Event) => (item.my_registration ? "Registered" : "") }]),
    { header: "Status", cell: (item) => <StatusDot {...eventState(item, today)} />, exportValue: (item) => eventState(item, today).label },
  ];
  const signUp = row ? canRegister(row, today) : undefined;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Events"
        description={isOffice ? "Reunions and other alumni events. Open one to see who has registered, record payments and check people in." : "Reunions and other events for former pupils. Open one to read about it and register."}
        actions={isOffice && <button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" onClick={() => { save.reset(); setForm("new"); }}><CalendarPlus className="h-4 w-4" /> New event</button>}
        rows={rows}
        config={eventConfig(today)}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        openLabel={(item) => (!isOffice && hasProfile && canRegister(item, today).ok ? "Register" : "Open")}
        noun={["event", "events"]}
        exportName={{ file: "alumni-events", title: "Alumni Events" }}
        canExport={isOffice}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={isOffice ? "No events yet." : "No events are coming up just now."}
      />
      {row && !form && (
        <DetailsModal
          size="3xl"
          title={row.name}
          headline={{ label: labelOf(EVENT_TYPES, row.event_type), value: day(row.event_date), note: `${time(row.start_time)}–${time(row.end_time)}${row.venue ? ` · ${row.venue}` : ""}` }}
          status={eventState(row, today)}
          facts={[
            ["Fee", Number(row.registration_fee) ? ugx(row.registration_fee) : "Free"],
            ["Register by", day(row.registration_deadline) || "Any time before"],
            ["Places", row.max_attendees ? `${row.max_attendees}${isOffice ? ` (${row.registered_count ?? 0} taken)` : ""}` : "No limit"],
            ["Contact", [row.contact_phone, row.contact_email].filter(Boolean).join(" · ") || row.organizer_name],
            ...(row.my_registration ? [["Your registration", `${paymentLabel(row.my_registration.payment_status as "pending")}${row.my_registration.attended ? " · attended" : ""}`] as [string, string]] : []),
          ]}
          sections={[{ title: "About it", body: row.description }, { title: "Programme", body: row.agenda }, { title: "Address", body: row.address }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {isOffice && <button type="button" disabled={setActive.isPending} className="mr-auto btn btn-outline" onClick={() => (row.is_active ? window.confirm(`Cancel ${row.name}?`) : true) && setActive.mutate(row)}>{row.is_active ? "Cancel event" : "Restore event"}</button>}
              {!isOffice && row.my_registration && !row.my_registration.attended && row.my_registration.payment_status !== "paid" && row.event_date >= today && (
                <button type="button" disabled={cancel.isPending} className="mr-auto rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50" onClick={() => window.confirm("Cancel your registration?") && cancel.mutate(row.my_registration!.id)}>Cancel my registration</button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {isOffice && <button type="button" className="btn btn-primary gap-1.5" onClick={() => { save.reset(); setForm("edit"); }}><Pencil className="h-4 w-4" /> Edit</button>}
              {!isOffice && hasProfile && signUp?.ok && <button type="button" className="btn btn-primary" onClick={() => { register.reset(); setForm("register"); }}>Register</button>}
            </>
          }
        >
          {!isOffice && hasProfile && signUp && !signUp.ok && !row.my_registration && <p className="text-sm text-gray-600">{signUp.reason}</p>}
          {isOffice && (
            <section className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Registered ({registrations.data?.length ?? row.registered_count ?? 0})</h4>
              {registrations.isLoading ? <div className="h-8 animate-pulse rounded bg-gray-100" /> : registrations.data?.length ? (
                <div className="sheet-wrap max-h-72 overflow-y-auto">
                  <table className="sheet">
                    <thead><tr><th>Name</th><th>Class of</th><th>Payment</th><th>Needs</th><th className="text-right">Action</th></tr></thead>
                    <tbody>
                      {registrations.data.map((item) => (
                        <tr key={item.id}>
                          <td className="font-medium text-gray-900">{item.alumni_name}</td>
                          <td>{item.graduation_year}</td>
                          <td>{paymentLabel(item.payment_status)}</td>
                          <td className="max-w-xs truncate">{[item.dietary_requirements, item.special_requests].filter(Boolean).join("; ") || "—"}</td>
                          <td className="whitespace-nowrap text-right">
                            {item.payment_status === "pending" && <button type="button" disabled={act.isPending} className="mr-2 text-xs font-medium text-primary-700 hover:underline" onClick={() => act.mutate({ id: item.id, action: "paid" })}>Mark paid</button>}
                            {item.attended ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> In {time(item.check_in_time)}</span> : <button type="button" disabled={act.isPending} className="text-xs font-medium text-primary-700 hover:underline" onClick={() => act.mutate({ id: item.id, action: "in" })}>Check in</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-sm text-gray-500">Nobody has registered yet.</p>}
            </section>
          )}
        </DetailsModal>
      )}
      {form === "new" && <EventForm busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The event could not be saved.") : undefined} onSave={(data) => save.mutate({ data })} onClose={() => setForm(null)} />}
      {form === "edit" && row && <EventForm record={row} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The event could not be saved.") : undefined} onSave={(data) => save.mutate({ id: row.id, data })} onClose={() => setForm(null)} />}
      {form === "register" && row && <RegisterForm event={row} busy={register.isPending} error={register.isError ? apiErrorMessage(register.error, "You could not be registered.") : undefined} onSave={(data) => register.mutate(data)} onClose={() => setForm(null)} />}
    </div>
  );
}
